import React, { useState, useEffect, useCallback } from 'react';
import { 
  Image, Upload, Search, X, Check, Loader2, Trash2, 
  FolderOpen, Grid, List as ListView
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

const MediaLibrary = ({ open, onOpenChange, onSelect }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [altText, setAltText] = useState('');
  const [view, setView] = useState('grid');

  const getAuthHeader = () => {
    const token = localStorage.getItem('vipgez_admin_token') || localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchImages = useCallback(async (resetList = false) => {
    setLoading(true);
    try {
      const currentPage = resetList ? 1 : page;
      const params = new URLSearchParams({ page: currentPage, limit: 20 });
      if (search) params.set('query', search);
      
      const response = await fetch(`${API_BASE}/api/cms/media?${params}`, {
        headers: getAuthHeader()
      });
      
      const data = await response.json();
      
      if (data.success) {
        if (resetList) {
          setImages(data.images || []);
          setPage(1);
        } else {
          setImages(prev => [...prev, ...(data.images || [])]);
        }
        setHasMore(data.hasMore || false);
      }
    } catch (error) {
      console.error('Failed to fetch images:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    if (open) {
      fetchImages(true);
    }
  }, [open]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchImages(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP, and GIF images are allowed');
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Simulate progress (actual XHR would have real progress)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);
      
      const response = await fetch(`${API_BASE}/api/cms/media/upload`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: formData
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Image uploaded successfully');
        fetchImages(true);
      } else {
        toast.error(data.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/cms/media/${imageId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Image deleted');
        setImages(prev => prev.filter(img => img.id !== imageId));
        if (selectedImage?.id === imageId) {
          setSelectedImage(null);
        }
      }
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleSelect = () => {
    if (!selectedImage) return;
    
    if (!altText.trim()) {
      toast.error('Please enter alt text for accessibility');
      return;
    }
    
    // Ensure full URL for content insertion
    const fullUrl = selectedImage.url.startsWith('/') 
      ? `${API_BASE}${selectedImage.url}` 
      : selectedImage.url;
    
    onSelect(fullUrl, altText);
    onOpenChange(false);
    setSelectedImage(null);
    setAltText('');
  };

  const loadMore = () => {
    setPage(prev => prev + 1);
    fetchImages(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-secondary border-border max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Media Library
          </DialogTitle>
        </DialogHeader>

        {/* Toolbar */}
        <div className="flex items-center gap-4 py-4 border-b border-border">
          {/* Upload */}
          <div className="relative">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleUpload}
              className="absolute inset-0 opacity-0 cursor-pointer"
              disabled={uploading}
            />
            <Button disabled={uploading} className="gap-2">
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {uploadProgress}%
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Upload Image
                </>
              )}
            </Button>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search images..."
                className="pl-10 bg-background text-foreground"
              />
            </div>
            <Button type="submit" variant="outline">Search</Button>
          </form>

          {/* View Toggle */}
          <div className="flex border border-border rounded-lg overflow-hidden">
            <button
              onClick={() => setView('grid')}
              className={`p-2 ${view === 'grid' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setView('list')}
              className={`p-2 ${view === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
            >
              <ListView className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Images Grid/List */}
        <div className="flex-1 overflow-auto py-4">
          {loading && images.length === 0 ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Image className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No images found. Upload your first image!</p>
            </div>
          ) : view === 'grid' ? (
            <div className="grid grid-cols-4 gap-4">
              {images.map((image) => (
                <div
                  key={image.id}
                  onClick={() => {
                    setSelectedImage(image);
                    setAltText(image.alt || '');
                  }}
                  className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                    selectedImage?.id === image.id 
                      ? 'border-primary ring-2 ring-primary/30' 
                      : 'border-transparent hover:border-muted-foreground/30'
                  }`}
                >
                  <img
                    src={image.url.startsWith('/') ? `${API_BASE}${image.url}` : image.url}
                    alt={image.alt || image.filename}
                    className="w-full h-32 object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(image.id);
                      }}
                      className="p-2 bg-red-500 rounded-full hover:bg-red-600"
                    >
                      <Trash2 className="h-4 w-4 text-white" />
                    </button>
                  </div>
                  {selectedImage?.id === image.id && (
                    <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                      <Check className="h-3 w-3" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {images.map((image) => (
                <div
                  key={image.id}
                  onClick={() => {
                    setSelectedImage(image);
                    setAltText(image.alt || '');
                  }}
                  className={`flex items-center gap-4 p-3 rounded-lg cursor-pointer transition-all ${
                    selectedImage?.id === image.id 
                      ? 'bg-primary/10 border border-primary' 
                      : 'hover:bg-muted border border-transparent'
                  }`}
                >
                  <img
                    src={image.url.startsWith('/') ? `${API_BASE}${image.url}` : image.url}
                    alt={image.alt || image.filename}
                    className="w-16 h-16 object-cover rounded"
                    loading="lazy"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground font-medium truncate">{image.filename}</p>
                    <p className="text-xs text-muted-foreground">
                      {image.size ? `${(image.size / 1024).toFixed(1)} KB` : ''} 
                      {image.uploaded_at && ` • ${new Date(image.uploaded_at).toLocaleDateString()}`}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(image.id);
                    }}
                    className="p-2 text-red-500 hover:bg-red-500/10 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Load More */}
          {hasMore && !loading && (
            <div className="text-center mt-4">
              <Button variant="outline" onClick={loadMore}>
                Load More
              </Button>
            </div>
          )}
        </div>

        {/* Selection Info & Alt Text */}
        {selectedImage && (
          <div className="border-t border-border pt-4 space-y-3">
            <div className="flex items-center gap-4">
              <img
                src={selectedImage.url.startsWith('/') ? `${API_BASE}${selectedImage.url}` : selectedImage.url}
                alt={selectedImage.alt || 'Selected'}
                className="w-20 h-20 object-cover rounded"
              />
              <div className="flex-1">
                <p className="text-foreground font-medium">{selectedImage.filename}</p>
                <p className="text-sm text-muted-foreground truncate">{selectedImage.url}</p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">
                Alt Text <span className="text-red-500">*</span>
              </label>
              <Input
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
                placeholder="Describe this image for accessibility..."
                className="bg-background text-foreground"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Alt text is required for accessibility and SEO
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="border-t border-border pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSelect} 
            disabled={!selectedImage || !altText.trim()}
          >
            <Check className="h-4 w-4 mr-2" />
            Insert Image
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MediaLibrary;
